import styled, { keyframes, type DefaultTheme } from 'styled-components';
import { riseIn } from '@shared/theme/animations';

/* Общие элементы дашборда «Обзор» ОСНОВА.Кадры — в стиле обзора ОСНОВА.Юрист. */

export type Tone = 'default' | 'success' | 'warning' | 'danger' | 'info';

export function toneColor(theme: DefaultTheme, tone: Tone = 'default'): string {
  switch (tone) {
    case 'success':
      return theme.colors.status.success;
    case 'warning':
      return theme.colors.status.warning;
    case 'danger':
      return theme.colors.status.danger;
    case 'info':
      return theme.colors.status.info;
    default:
      return theme.colors.text.primary;
  }
}

export const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  margin: ${({ theme }) => theme.spacing(6)} 0 ${({ theme }) => theme.spacing(3)};
  /* Переход по якорю с плиток не прячет подпись под шапкой. */
  scroll-margin-top: ${({ theme }) => theme.spacing(16)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut};
`;

export const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(260px, 100%), 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const TwoCols = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(360px, 100%), 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
  align-items: stretch;
`;

const tileEntry = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Tile = styled.button<{ $delay: number }>`
  position: relative;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  min-height: 164px;
  padding: ${({ theme }) => theme.spacing(5)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  animation: ${tileEntry} 480ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $delay }) => `${$delay}ms`};
  transition:
    transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadow.lg};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

export const TileHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export const TileIcon = styled.div`
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
`;

export const TileTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const TileNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

export const BigNumber = styled.div<{ $tone?: Tone }>`
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: ${({ theme, $tone }) => toneColor(theme, $tone)};
  font-variant-numeric: tabular-nums;
`;

export const Metrics = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  margin-top: auto;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Metric = styled.div<{ $tone?: Tone }>`
  display: flex;
  flex-direction: column;
  gap: 2px;

  & > strong {
    font-size: 15px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${({ theme, $tone }) => toneColor(theme, $tone)};
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50% { transform: scale(1.9); opacity: 0; }
`;

export const LivePulse = styled.span`
  position: relative;
  display: inline-block;
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.success};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.status.success};
    animation: ${pulse} 1.4s ease-out infinite;
  }
`;

export const Panel = styled.section<{ $delay?: number }>`
  min-width: 0;
  padding: ${({ theme }) => theme.spacing(5)};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $delay = 0 }) => `${$delay}ms`};
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  & > h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & > span {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

export const Hint = styled.p`
  margin: ${({ theme }) => theme.spacing(3)} 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/** «< 1 ч», «5 ч», «3 дн» — для медиан времени ответа. */
export function fmtHours(h: number | null | undefined): string {
  if (h == null) return '—';
  if (h < 1) return '< 1 ч';
  if (h < 48) return `${Math.round(h)} ч`;
  return `${Math.round(h / 24)} дн`;
}

/** «5 мин назад», «3 ч назад», «2 дн назад»; пусто — «—». */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} ч назад`;
  return `${Math.round(h / 24)} дн назад`;
}

/** «через 40 мин», «через 3 ч»; прошедшее время — «в ближайшую минуту». */
export function inTime(iso: string): string {
  const min = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  if (min < 1) return 'в ближайшую минуту';
  if (min < 60) return `через ${min} мин`;
  const h = Math.round(min / 60);
  if (h < 48) return `через ${h} ч`;
  return `через ${Math.round(h / 24)} дн`;
}

/** Доля в процентах без дробей; пустой знаменатель — «—». */
export function pct(part: number, whole: number): string {
  if (!whole) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}
